import { useEffect, useMemo, useRef } from 'react';
import type {
  QueryKey,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  UseQueryOptions,
  UseQueryResult,
  UseSuspenseQueryOptions,
  UseSuspenseQueryResult,
} from '@tanstack/react-query';
import { useSuspenseQuery } from '@tanstack/react-query';
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type { Api } from '../makeApiProvider';

export const useSuspenseApiQuery = <A extends Api | undefined, T, U>(
  useApi: () => A,
  key: QueryKey,
  callback: (api: A) => Promise<U>,
  options?: Omit<UseSuspenseQueryOptions<U, unknown, T>, 'queryKey' | 'queryFn'>
): UseSuspenseQueryResult<T, unknown> => {
  const api = useApi();

  // Store latest api in ref to avoid stale closures
  const apiRef = useRef(api);
  apiRef.current = api;

  const queryClient = useQueryClient();

  const keyRef = useRef(key);
  keyRef.current = key;

  // Cancel queries on unmount
  useEffect(() => {
    return () => {
      queryClient.cancelQueries({ queryKey: keyRef.current });
    };
  }, [queryClient]);

  const result = useSuspenseQuery({
    queryKey: key,
    queryFn: async () => {
      // Always use the latest api from ref, not the closure
      const currentApi = apiRef.current;
      const currentToken = currentApi?.authContext?.getTokenImmediate?.();
      const currentInitializing = currentApi?.authContext?.initializing;

      // If auth is still initializing, suspend the query until initialization completes
      if (currentInitializing) {
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        throw new Promise(() => {}); // Suspend until auth completes
      }

      if (currentToken === undefined) {
        throw new Error('No token available');
      }
      return await callback(currentApi);
    },
    ...options,
  });

  const { refetch } = result;

  const userId = useMemo(
    () => api?.authContext?.user?.userId,
    [api?.authContext]
  );

  const isIdleRef = useRef(result.fetchStatus === 'idle');

  useEffect(() => {
    if (userId !== undefined && isIdleRef.current) {
      refetch();
    }
  }, [userId, refetch]);

  return result;
};

export const useApiQuery = <A extends Api | undefined, T, U>(
  useApi: () => A,
  key: QueryKey,
  callback: (api: A) => Promise<U>,
  options?: Omit<UseQueryOptions<U, unknown, T>, 'queryKey' | 'queryFn'>
): UseQueryResult<T, unknown> => {
  const api = useApi();

  const enabled =
    (options?.enabled ?? true) &&
    !api?.authContext.initializing &&
    api?.authContext.getTokenImmediate() !== undefined;

  const result = useQuery({
    queryKey: key,
    queryFn: () => {
      if (!enabled) {
        throw new Error('Query fetched even thou it is disabled');
      }

      return callback(api);
    },
    ...options,
    enabled,
  });

  const { refetch } = result;

  const userId = useMemo(
    () => api?.authContext?.user?.userId,
    [api?.authContext]
  );

  const isIdleRef = useRef(result.isLoading);

  useEffect(() => {
    const transitionedFromIdle = isIdleRef.current && !result.isLoading;
    if (userId !== undefined && transitionedFromIdle && enabled) {
      refetch();
    }
  }, [userId, result.isLoading, refetch, enabled]);

  useEffect(() => {
    if (isIdleRef.current !== result.isLoading) {
      isIdleRef.current = result.isLoading;
    }
  }, [result.isLoading]);

  return result;
};

/**
 * Same as useApiQuery, but allows to refetch when enabled is false (like useQuery default behaviour)
 */
export const useApiQueryNoRefetchOverride = <A extends Api | undefined, T, U>(
  useApi: () => A,
  key: QueryKey,
  callback: (api: A) => Promise<U>,
  options?: Omit<UseQueryOptions<U, unknown, T>, 'queryKey' | 'queryFn'>
): UseQueryResult<T, unknown> => {
  const api = useApi();

  const enabled =
    !api?.authContext.initializing &&
    api?.authContext.getTokenImmediate() !== undefined
      ? options?.enabled
      : false;

  const result = useQuery({
    queryKey: key,
    queryFn: () => {
      if (api?.authContext?.getTokenImmediate() === undefined) {
        return undefined as unknown as U;
      }
      return callback(api);
    },
    ...options,
    enabled,
  });

  const { refetch } = result;

  const userId = useMemo(
    () => api?.authContext?.user?.userId,
    [api?.authContext]
  );

  const isIdleRef = useRef(result.isLoading);

  useEffect(() => {
    const transitionedFromIdle = isIdleRef.current && !result.isLoading;
    if (userId !== undefined && transitionedFromIdle && enabled) {
      refetch();
    }
  }, [userId, result.isLoading, refetch, enabled]);

  useEffect(() => {
    if (isIdleRef.current !== result.isLoading) {
      isIdleRef.current = result.isLoading;
    }
  }, [result.isLoading]);

  return result;
};

export const usePrefetchApiQuery = <A extends Api, T>(
  useApi: <Optional extends boolean = false>(
    optional?: Optional
  ) => Optional extends true ? A | undefined : A,
  key: QueryKey,
  callback: (api: A) => Promise<T>,
  enabled?: boolean
): void => {
  // Always call with optional=true since we handle undefined in the runtime check
  const api = useApi(true);
  const client = useQueryClient();

  useEffect(() => {
    // Only prefetch when:
    // 1. Feature is enabled (undefined is treated as true)
    // 2. API instance exists
    // 3. User is authenticated
    const shouldPrefetch = enabled ?? true;
    if (!shouldPrefetch || !api || !api.authContext?.user?.userId) {
      return;
    }

    void client.prefetchQuery({
      queryKey: key,
      queryFn: () => callback(api),
    });
  }, [api, api?.authContext?.user?.userId, callback, client, enabled, key]);
};

export const usePhoenixApiInfiniteQuery = <
  A extends Api | undefined,
  T,
  U extends {
    meta: {
      current_page: number;
      total_pages: number;
      has_next_page: boolean;
      next_page: number | null;
      previous_page: number | null;
      has_previous_page: boolean;
      page_size: number;
      total_count: number;
    };
  }
>(
  useApi: () => A,
  key: QueryKey,
  callback: (api: A, page: number) => Promise<U>,
  options?: Omit<
    UseInfiniteQueryOptions<U, unknown, T, QueryKey, number>,
    | 'queryKey'
    | 'queryFn'
    | 'getNextPageParam'
    | 'getPreviousPageParam'
    | 'initialPageParam'
  >
): UseInfiniteQueryResult<T, unknown> => {
  const api = useApi();

  const enabled =
    (options?.enabled ?? true) &&
    !api?.authContext.initializing &&
    api?.authContext.getTokenImmediate() !== undefined;

  const result = useInfiniteQuery<U, unknown, T, QueryKey, number>({
    queryKey: key,
    queryFn: ({ pageParam }) => {
      if (
        options?.enabled === false ||
        api?.authContext.getTokenImmediate() === undefined
      ) {
        return undefined as unknown as U;
      }
      return callback(api, pageParam);
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (lastPage?.meta?.has_next_page) {
        return lastPage.meta.next_page;
      }
      return undefined;
    },
    getPreviousPageParam: (firstPage) => {
      if (firstPage?.meta?.has_previous_page) {
        return firstPage.meta.previous_page;
      }
      return undefined;
    },
    ...options,
    enabled,
  });

  const { refetch } = result;

  const userId = useMemo(
    () => api?.authContext.user?.userId,
    [api?.authContext]
  );

  useEffect(() => {
    if (userId !== undefined && !result.isLoading) {
      refetch();
    }
  }, [userId, result.isLoading, refetch]);

  return result;
};
